import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useImportAttendance } from '../../hooks/use-manage-attendance';
import { AttendanceImportResult } from '../../services/manage-attendance.service';
import {
  DEVICE_EXPORT_HEADERS,
  ImportFormat,
  ImportParseResult,
  parseAttendanceCsv,
} from '../../utils/attendance-import';

interface ImportAttendanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Most row problems a list shows before "and N more". */
const LIST_LIMIT = 50;

/**
 * Attendance import (ATTENDANCE.IMPORT): pick the file's format, check it here (every row problem is
 * listed before upload), then import. Days that already have a record are never changed; employees
 * outside the importer's departments are left out. The result lists every row that was not saved.
 */
export const ImportAttendanceDialog = ({ open, onOpenChange }: ImportAttendanceDialogProps) => {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ImportFormat>('doorLog');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ImportParseResult | null>(null);
  const [result, setResult] = useState<AttendanceImportResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: importRows, isPending } = useImportAttendance();

  useEffect(() => {
    if (!open) {
      setFileName('');
      setParsed(null);
      setResult(null);
    }
  }, [open]);

  const handleFile = async (file: File | undefined) => {
    setResult(null);
    if (!file) {
      setParsed(null);
      setFileName('');
      return;
    }
    setFileName(file.name);
    setParsed(parseAttendanceCsv(format, await file.text()));
  };

  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    handleFile(undefined);
  };

  const handleImport = async () => {
    if (!parsed || parsed.rows.length === 0) return;
    try {
      setResult(await importRows({ format, fileName, rows: parsed.rows }));
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const problems = [
    ...(parsed?.errors ?? []).map((e) => ({ row: e.row, text: t(e.messageKey) })),
    ...(result?.skipped ?? []).map((s) => ({
      row: s.Row,
      text: `${s.EmployeeId} ${s.Day}: ${t(`IMPORT_SKIPPED_${s.Reason}`, { defaultValue: s.Reason })}`,
    })),
  ].sort((a, b) => a.row - b.row);

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('IMPORT_ATTENDANCE')}</DialogTitle>
          <DialogDescription>
            {t(format === 'doorLog' ? 'IMPORT_DOOR_LOG_HINT' : 'IMPORT_DEVICE_EXPORT_HINT', {
              headers: DEVICE_EXPORT_HEADERS.join(', '),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="attendance-import-format">{t('IMPORT_FORMAT')}</Label>
            <Select
              value={format}
              onValueChange={(next) => {
                setFormat(next as ImportFormat);
                setParsed(null);
                setResult(null);
                setFileName('');
              }}
            >
              <SelectTrigger id="attendance-import-format" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="doorLog">{t('IMPORT_FORMAT_DOOR_LOG')}</SelectItem>
                <SelectItem value="deviceExport">{t('IMPORT_FORMAT_DEVICE_EXPORT')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="attendance-import-file">{t('IMPORT_FILE')}</Label>
            <input
              ref={fileInputRef}
              key={format}
              id="attendance-import-file"
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              disabled={isPending}
              onChange={(event) => handleFile(event.target.files?.[0])}
            />
            {fileName ? (
              <div className="flex items-center gap-3 rounded-md border bg-card px-3 py-2.5">
                <FileText className="h-5 w-5 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium" title={fileName}>
                  {fileName}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('CHANGE')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t('REMOVE')}
                  disabled={isPending}
                  onClick={clearFile}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label
                htmlFor="attendance-import-file"
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  handleFile(event.dataTransfer.files?.[0]);
                }}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-4 py-6 text-center transition-colors hover:border-primary hover:bg-accent',
                  dragging && 'border-primary bg-primary/5'
                )}
              >
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm">
                  {t('DRAG_N_DROP_YOUR_FILE_HERE_OR_BROWSE_TO_UPLOAD')}
                </span>
                <span className="text-xs text-muted-foreground">{t('IMPORT_FILE_HINT')}</span>
              </label>
            )}
          </div>

          {parsed?.fileError && <p className="text-sm text-destructive">{t(parsed.fileError)}</p>}
          {parsed && !parsed.fileError && !result && (
            <p className="text-sm">
              {t('IMPORT_READY', { days: parsed.rows.length, problems: parsed.errors.length })}
            </p>
          )}
          {result && (
            <p className="text-sm font-medium">
              {t('IMPORT_RESULT', { saved: result.saved, skipped: result.skipped.length })}
            </p>
          )}

          {problems.length > 0 && (
            <div className="max-h-56 overflow-y-auto rounded-md border p-2 text-xs">
              {problems.slice(0, LIST_LIMIT).map((problem, index) => (
                <div key={`${problem.row}-${index}`} className="py-0.5">
                  <span className="font-medium">
                    {t('ROW')} {problem.row}:
                  </span>{' '}
                  {problem.text}
                </div>
              ))}
              {problems.length > LIST_LIMIT && (
                <div className="pt-1 text-muted-foreground">
                  {t('AND_N_MORE', { count: problems.length - LIST_LIMIT })}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* After an import Close is the only button left, so it stays on a phone too. */}
          <DialogCancelButton
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className={result ? 'max-md:inline-flex' : undefined}
          >
            {t(result ? 'CLOSE' : 'CANCEL')}
          </DialogCancelButton>
          {!result && (
            <Button
              onClick={handleImport}
              loading={isPending}
              disabled={
                isPending || !parsed || Boolean(parsed.fileError) || parsed.rows.length === 0
              }
            >
              {t('IMPORT')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
